// Shared capture+share state machine for the post-session share card
// (2026-08-18 lift): both platforms mount a <ShareCardRenderer ref={cardRef}>
// off-screen and need the identical sharing-in-flight bookkeeping around
// `cardRef.current.capture()`. Only WHAT happens with the resulting URI
// differs (native share sheet vs. Web Share API / download), so that step is
// the one thing each host injects — same "seam from the mount" rule as
// confirm/notify (apps/web-rnw/src/confirm.ts).
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import type { Ref, RefObject } from 'react';
import type { ViewShotRef } from 'react-native-view-shot';

import { reportShareError } from '../shareError';
import type { PostSessionSummaryHandle } from './usePostSaveSummary';

export function useShareCardHandle(
  handleRef: Ref<PostSessionSummaryHandle>,
  doShare: (uri: string) => Promise<void>,
  onError?: (message: string) => void,
): {
  cardRef: RefObject<ViewShotRef | null>;
  sharing: boolean;
  onShare: () => Promise<void>;
} {
  const cardRef = useRef<ViewShotRef>(null);
  const [sharing, setSharing] = useState(false);

  const onShare = useCallback(async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const ref = cardRef.current;
      if (!ref || typeof ref.capture !== 'function') {
        throw new Error('capture_not_ready');
      }
      const uri = await ref.capture();
      await doShare(uri);
    } catch (err) {
      reportShareError(err, onError);
    } finally {
      setSharing(false);
    }
  }, [sharing, doShare, onError]);

  useImperativeHandle(
    handleRef,
    () => ({ share: onShare, isSharing: () => sharing }),
    [onShare, sharing],
  );

  return { cardRef, sharing, onShare };
}
